import { forwardRef, useCallback, useEffect, useState } from 'react';

import { useTranslation } from '@klv/hooks/useTranslation';

import Icon from '@klv/atoms/icon';
import { UniversalMenuItem } from '@klv/molecules/universal-menu-item';
import { UniversalMenuItemWithOptimizely } from '@klv/molecules/universal-menu-item/withOptimizely';
import { LanguageSelector } from '@klv/molecules/language-selector';
import { generateClassName } from '@klv/shared/utils/functions';

import { gtmClick } from '../../utils';
import { UniversalNavigationDrawer } from '../drawer';

import * as parentStyles from '../../styles/universalNavigation.module.css';
import * as styles from './styles/mobile.module.css';

import type { IUniversalNavigation } from '../../types';
import type { TSiteLanguageAndDetails, ITranslation } from '@klaviyo-mkt/marketing-cms/src/staticTypes';
import type { TBackgroundColor } from '@klv/organisms/universal-footer/types';

type TOmittedFields = 'children' | 'color';

const MobileMenuItem = ({
  childItems,
  main,
  submenu,
}: {
  childItems?: IUniversalNavigation['menuItems'];
  main?: React.ReactNode;
  submenu?: React.ReactNode;
}) => childItems?.length ? (
  <details>
    <summary>
      {main}
    </summary>
    {submenu}
  </details>
) : main;

const Mobile = ({
  className,
  ctas = [],
  menuItems = [],
  menuStyle = 'default',
  path = '',
  platformActions = [],
  scrollDirection,
  siteActions = [],
  utilityItems = [],
  sites = [],
  translations = [],
}: Omit<IUniversalNavigation, TOmittedFields> & {
  backgroundColor?: TBackgroundColor;
  currentLanguage?: string;
  sites?: TSiteLanguageAndDetails[];
  translations?: ITranslation[];
}, ref: React.ForwardedRef<HTMLDialogElement>) => {
  const [hide, setHide] = useState(false);
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n?.language?.toLowerCase();

  const onClose = useCallback(() => {
    if (typeof ref !== 'function' && ref?.current) {
      const dialog = ref.current;
      const animateOut = () => {
        setHide(false);
        dialog.close();
        dialog.removeEventListener('animationend', animateOut, false);
      };
      setHide(true);
      dialog.addEventListener('animationend', animateOut, false);
    }
  }, [ref]);

  useEffect(() => {
    if (typeof ref !== 'function' && ref?.current) {
      const listener = (event: MouseEvent | TouchEvent) => {
        if (
          event?.target
          && ref?.current
          && ref?.current?.open
          && (event?.target as HTMLElement)?.tagName === ref?.current?.tagName
        ) {
          onClose();
        }

        return;
      };

      document.addEventListener('mousedown', listener);
      document.addEventListener('touchstart', listener);

      return () => {
        document.removeEventListener('mousedown', listener);
        document.removeEventListener('touchstart', listener);
      };
    }
  }, [onClose, ref]);

  const logoLink = menuItems?.[0]?.url || '/';
  const logoTitle = menuItems?.[0]?.label || t('organisms_navigation_logo_label');

  return (
    <dialog ref={ref} className={generateClassName([className, menuStyle === 'drilldown' && styles.drilldown, menuStyle === 'subnav' && styles.subnav, scrollDirection === 'down' && styles.scrolled, className?.includes('peakaboo') && styles.peakaboo, styles.mobileMenuDialog, hide && styles.hide])}>
      {menuStyle === 'subnav' ? (
        <div className={styles.subnavHeader}>
          <div>
            <a className={styles.subnavHeaderLabel} href={logoLink} title={logoTitle}>
              <Icon
                className={styles.flag}
                color='currentColor'
                iconName='BugMarkFilled'
                size={3}
              />
              {menuItems?.[0]?.label && <h2 className={generateClassName(['h3', 'semibold', styles.subnavHeaderTitle])}>{logoTitle}</h2>}
            </a>
          </div>
          <button className={generateClassName([styles.close])} onClick={() => onClose()} autoFocus />
        </div>
      ) : (
        <div className={styles.closeContainer}>
          <button className={styles.close} onClick={() => onClose()} autoFocus />
        </div>
      )}
      <nav className={styles.nav}>
        <ul className={styles.list}>
          {menuItems.map(({ childItems, id, label, url: href = '' }, index) => {
            if (index === 0 && menuStyle === 'subnav') return null;

            return (
              <li
                className={generateClassName([
                  styles.listItem,
                  childItems?.length && styles.hasChildren,
                ])}
                key={id}
              >
                <MobileMenuItem
                  childItems={childItems}
                  main={(
                    <UniversalMenuItemWithOptimizely
                      aria-current={
                        (childItems?.some(({ url }) => url === path) && 'true')
                        || href === path
                        || undefined
                      }
                      as={(href === '#' || href === '' || ((menuStyle === 'drilldown' || menuStyle === 'subnav') && childItems?.length)) ? 'div' : undefined}
                      className={generateClassName([
                        styles.link,
                        childItems?.length && styles.hasChildren,
                      ])}
                      featured={true}
                      href={(href === '#' || href === '' || ((menuStyle === 'drilldown' || menuStyle === 'subnav') && childItems?.length)) ? undefined : href}
                      onClick={() => { gtmClick({ childItem: label }); }}
                    >
                      <span>{label}</span>
                    </UniversalMenuItemWithOptimizely>
                  )}
                  submenu={
                    <UniversalNavigationDrawer
                      menuItems={childItems}
                      menuStyle={menuStyle}
                      parentLabel={label}
                    />
                  }
                />
              </li>
            );
          })}
        </ul>
        <ul className={generateClassName([styles.list, styles.utilityList])}>
          {utilityItems.map(({ id, url, ...rest }) => (
            <li key={id}>
              <UniversalMenuItem
                className={generateClassName([
                  styles.utilityLink,
                ])}
                href={url}
                {...rest}
              />
            </li>
          ))}
          {siteActions.map(({ icon, label, ...rest }) => (
            <li key={label}>
              <UniversalMenuItem
                className={generateClassName([
                  styles.utilityLink,
                ])}
                iconName={icon}
                label={label}
                {...rest}
              />
            </li>
          ))}
        </ul>
        <ul className={generateClassName([styles.list, styles.ctaList])}>
          <div className={styles.languageSelectorContainer}>
            <LanguageSelector
                currentLanguage={currentLanguage}
                sites={sites}
                translations={translations}
                isDrawer={true}
              />
          </div>
          {ctas.map(({ id, variant, url, ...rest }) => (
            <li key={id}>
              <UniversalMenuItem
                className={generateClassName([
                  parentStyles.cta,
                  variant && parentStyles[variant],
                ])}
                href={url}
                {...rest}
              />
            </li>
          ))}
          {platformActions.map(({ component, label, ...rest }) => (
            <li key={label}>
              {component ? component : (
                <UniversalMenuItem
                  label={label}
                  {...rest}
                />
              )}
            </li>
          ))}
        </ul>
      </nav>
    </dialog>
  );
};

export const UniversalNavigationMobile = forwardRef(Mobile);
